// ============================================================
// SecaoTestesFisicos.js — a tela dos testes de campo
//
// Mesmo desenho das outras duas telas de avaliação: lista de atletas,
// ficha com a série, formulário na mesma tela. Nenhuma conta mora aqui,
// tudo vem de `testesFisicos.js` e o escore global de `desempenho.js`.
//
// ── Duas coisas no mesmo lançamento ─────────────────────────
//
// Esta tela lança as duas metades da coleta de campo, porque elas são a
// mesma ida à pista: os testes medidos, em centímetro, repetição e
// segundo, e as quatro notas de capacidade física, de 1 a 10. É o
// preparador que faz as duas, no mesmo dia, e as notas são dadas olhando
// os resultados que ele acabou de anotar.
//
// ── A ponte entre as duas ───────────────────────────────────
//
// A tabela de conversão que a coordenação escreveu no rodapé da planilha
// liga uma coisa à outra: impulsão de 1,85 m num atleta de BMX de 13
// anos sugere nota entre 6 e 8. A tela mostra a sugestão ao lado do
// campo de nota, com a regra que a produziu, e não preenche sozinha. A
// tabela cobre dois testes por modalidade, e as capacidades são quatro.
//
// ── As três tentativas ──────────────────────────────────────
//
// Todo teste é feito em três tentativas e vale a melhor. O sistema
// guarda as três, e não só a escolhida, porque elas respondem o que a
// melhor sozinha não responde: se o atleta saltou 184, 173 e 176, a
// melhor é 184 e a dispersão é de 11 cm, e num grupo que evolui 4% entre
// coletas isso diz que a melhora pode ser da tentativa boa.
// ============================================================

import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import {
  carregarMembrosGestao, carregarTestesFisicos, carregarAvaliacoesTecnicas,
  salvarTesteFisico,
} from "../firebase";
import {
  bateriaDa, AUSENCIAS, capacidadesSugeridas, compilarSerieDeTestes, laudoDosTestes,
  direcaoEmTexto,
  sugestaoDeNota, faixaDaNotaDeCapacidade, faixaEtaria, faixaEtariaDe, CONVERSOES,
  FAIXA_MORTA, TESTE_POR_ID,
} from "../testesFisicos";
import { compilarSerieTecnica } from "../avaliacaoTecnica";
import { escoreGlobal, serieDeEscores } from "../desempenho";
import { idadeEm, idadeDecimalEm } from "../idade";
import { gerarPDFTestesFisicos, gerarPDFFichaDeTestes } from "../pdfRelatorio";
import { useNomeNucleo } from "../NucleosContexto";
import {
  TEXTO, CINZA_ESC, BRANCO, BORDA, FUNDO_SUAVE, RAIO, ESP, TIPO,
  AZUL, OK, ATENCAO, RUIM,
} from "../tema";
import {
  Card, Btn, Sel, Input, StatusBox, Carregando, Vazio,
  Estatistica, FileiraDeEstatisticas, CampoDeBusca, GraficoDeMedida,
  EscoreGlobal, LegendaDosNiveis, LegendaDaEscala,
} from "../componentes";
import useIsMobile from "../useIsMobile";

const HOJE = () => new Date().toISOString().slice(0, 10);

// As marcas de ausência que o avaliador pode escolher no lugar de um
// número. Elas são informação, e não lixo: falta e dispensa médica dizem
// coisas diferentes sobre o atleta, e nenhuma das duas pode entrar numa
// média valendo zero.
const MARCAS = [
  { value: "", label: "resultado em número" },
  { value: "x", label: AUSENCIAS.x.rotulo },
  { value: "f", label: AUSENCIAS.f.rotulo },
  { value: "dm", label: AUSENCIAS.dm.rotulo },
];

export default function SecaoTestesFisicos({ userNucleo, podeEditar = true }) {
  const isMobile = useIsMobile();
  const nucleo = userNucleo || "";

  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);
  const [atletas, setAtletas] = useState([]);
  const [coletas, setColetas] = useState([]);
  const [tecnicas, setTecnicas] = useState([]);
  const [busca, setBusca] = useState("");
  const [modalidade, setModalidade] = useState("");
  const [selecionado, setSelecionado] = useState(null);

  const recarregar = useCallback(async () => {
    if (!nucleo) return;
    setCarregando(true);
    try {
      const [pessoas, testes, tec] = await Promise.all([
        carregarMembrosGestao(nucleo),
        carregarTestesFisicos(nucleo),
        carregarAvaliacoesTecnicas(nucleo),
      ]);
      setAtletas(pessoas.filter(p => (p.perfil || "").toLowerCase().includes("atleta")));
      setColetas(testes);
      setTecnicas(tec);
    } catch (e) {
      setStatus({ tipo: "erro", texto: `Não consegui carregar: ${e.message}` });
    } finally {
      setCarregando(false);
    }
  }, [nucleo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  const modalidades = useMemo(
    () => [...new Set(atletas.map(a => a.modalidade).filter(Boolean))].sort(),
    [atletas],
  );

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return atletas
      .filter(a => a.ativo !== false)
      .filter(a => !modalidade || a.modalidade === modalidade)
      .filter(a => !termo || (a.nome || "").toLowerCase().includes(termo))
      .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
  }, [atletas, busca, modalidade]);

  const porAtleta = useMemo(() => indexar(coletas), [coletas]);
  const tecnicasPorAtleta = useMemo(() => indexar(tecnicas), [tecnicas]);

  if (!nucleo) return <Vazio titulo="Selecione um núcleo." />;
  if (carregando) return <Carregando />;

  const atleta = selecionado ? atletas.find(a => a.uid === selecionado) : null;

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      {status && <StatusBox tipo={status.tipo} msg={status.texto} />}

      {atleta ? (
        <FichaDoAtleta
          atleta={atleta}
          coletas={porAtleta[atleta.uid] || []}
          tecnicas={tecnicasPorAtleta[atleta.uid] || []}
          nucleo={nucleo}
          podeEditar={podeEditar}
          isMobile={isMobile}
          onVoltar={() => setSelecionado(null)}
          onSalvou={async () => {
            await recarregar();
            setStatus({ tipo: "ok", texto: "Testes físicos gravados." });
          }}
          onErro={msg => setStatus({ tipo: "erro", texto: msg })}
        />
      ) : (
        <ListaDeAtletas
          lista={lista}
          porAtleta={porAtleta}
          tecnicasPorAtleta={tecnicasPorAtleta}
          modalidades={modalidades}
          modalidade={modalidade}
          setModalidade={setModalidade}
          busca={busca}
          setBusca={setBusca}
          nucleo={nucleo}
          onAbrir={setSelecionado}
          onErro={msg => setStatus({ tipo: "erro", texto: msg })}
        />
      )}
    </div>
  );
}

function indexar(lista) {
  const mapa = {};
  lista.forEach(x => {
    if (!x.atletaUid) return;
    (mapa[x.atletaUid] = mapa[x.atletaUid] || []).push(x);
  });
  return mapa;
}

// ── Lista ────────────────────────────────────────────────────

function ListaDeAtletas({
  lista, porAtleta, tecnicasPorAtleta, modalidades, modalidade, setModalidade,
  busca, setBusca, nucleo, onAbrir, onErro,
}) {
  const nomeDoNucleo = useNomeNucleo();
  const [gerando, setGerando] = useState(false);
  const comColeta = lista.filter(a => (porAtleta[a.uid] || []).length > 0).length;
  const soComTecnica = lista.filter(
    a => (tecnicasPorAtleta[a.uid] || []).length > 0 && !(porAtleta[a.uid] || []).length,
  ).length;

  async function baixarFichas() {
    setGerando(true);
    try {
      await gerarPDFFichaDeTestes({ atletas: lista, nucleo, nucleoNome: nomeDoNucleo(nucleo) });
    } catch (e) {
      onErro(`Não consegui gerar as fichas: ${e.message}`);
    } finally {
      setGerando(false);
    }
  }

  return (
    <Card>
      <FileiraDeEstatisticas>
        <Estatistica rotulo="Atletas" valor={lista.length} />
        <Estatistica rotulo="Com teste físico" valor={comColeta} />
        <Estatistica rotulo="Sem teste" valor={lista.length - comColeta} />
        <Estatistica rotulo="Com avaliação técnica e sem teste" valor={soComTecnica} />
      </FileiraDeEstatisticas>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: `${ESP.lg}px 0` }}>
        <CampoDeBusca valor={busca} onChange={setBusca} placeholder="Buscar atleta" />
        <Sel value={modalidade} onChange={setModalidade}
          options={[{ value: "", label: "Todas as modalidades" },
            ...modalidades.map(m => ({ value: m, label: m }))]} />
        {lista.length > 0 && (
          <Btn variant="secondary" onClick={baixarFichas} disabled={gerando}>
            {gerando ? "Gerando…" : `Fichas de coleta em papel (${lista.length})`}
          </Btn>
        )}
      </div>

      {lista.length === 0 ? (
        <Vazio titulo="Nenhum atleta neste recorte." />
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {lista.map(a => {
            const minhas = porAtleta[a.uid] || [];
            const ultima = minhas[minhas.length - 1];
            return (
              <button key={a.uid} onClick={() => onAbrir(a.uid)} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, width: "100%", textAlign: "left", cursor: "pointer",
                background: BRANCO, border: `1px solid ${BORDA}`, borderRadius: RAIO.md,
                padding: "11px 13px", font: "inherit",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: TEXTO, ...TIPO.corpo }}>{a.nome}</div>
                  <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                    {a.modalidade || "sem modalidade"}
                    {a.dataNascimento && ` · ${idadeEm(a.dataNascimento)} anos`}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ ...TIPO.apoio, color: minhas.length ? TEXTO : CINZA_ESC }}>
                    {minhas.length
                      ? `${minhas.length} colet${minhas.length > 1 ? "as" : "a"}`
                      : "sem teste"}
                  </div>
                  {ultima && (
                    <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                      última em {emBR(ultima.data)}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Ficha do atleta ──────────────────────────────────────────

function FichaDoAtleta({
  atleta, coletas, tecnicas, nucleo, podeEditar, isMobile,
  onVoltar, onSalvou, onErro,
}) {
  const nomeDoNucleo = useNomeNucleo();
  const [editando, setEditando] = useState(null);
  const [imprimindo, setImprimindo] = useState(false);
  const [gerandoFicha, setGerandoFicha] = useState(false);
  const [abertaEm, setAbertaEm] = useState(null);

  const idadeNa = useCallback(
    data => idadeDecimalEm(atleta.dataNascimento, data),
    [atleta.dataNascimento],
  );

  const serie = useMemo(
    () => compilarSerieDeTestes({
      modalidade: atleta.modalidade,
      idadeEm: idadeNa,
      coletas,
    }),
    [atleta.modalidade, idadeNa, coletas],
  );

  const serieTecnica = useMemo(
    () => compilarSerieTecnica({ modalidade: atleta.modalidade, avaliacoes: tecnicas }),
    [atleta.modalidade, tecnicas],
  );

  const escores = useMemo(
    () => serieDeEscores({ tecnica: serieTecnica, fisica: serie }),
    [serieTecnica, serie],
  );

  const ponto = serie.find(p => p.data === abertaEm) || serie[serie.length - 1] || null;
  const escoreDoPonto = ponto
    ? escores.find(e => e.dataFisica === ponto.data) || null
    : null;
  const laudo = ponto ? laudoDosTestes(ponto) : null;

  async function imprimir() {
    setImprimindo(true);
    try {
      await gerarPDFTestesFisicos({
        atleta, serie, escores, nucleo, nucleoNome: nomeDoNucleo(nucleo),
      });
    } catch (e) {
      onErro(`Não consegui gerar o PDF: ${e.message}`);
    } finally {
      setImprimindo(false);
    }
  }

  async function baixarFicha() {
    setGerandoFicha(true);
    try {
      await gerarPDFFichaDeTestes({
        atletas: [atleta], nucleo, nucleoNome: nomeDoNucleo(nucleo),
      });
    } catch (e) {
      onErro(`Não consegui gerar a ficha: ${e.message}`);
    } finally {
      setGerandoFicha(false);
    }
  }

  const semModalidade = !atleta.modalidade;
  const semNascimento = !atleta.dataNascimento;

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      <Card>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, flexWrap: "wrap",
        }}>
          <div>
            <Btn variant="discreto" tamanho="sm" onClick={onVoltar}>← Todos os atletas</Btn>
            <h2 style={{ margin: "6px 0 0", ...TIPO.titulo, color: TEXTO }}>{atleta.nome}</h2>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
              {atleta.modalidade || "sem modalidade"}
              {atleta.dataNascimento && ` · ${idadeEm(atleta.dataNascimento)} anos`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {serie.length > 0 && (
              <Btn variant="secondary" onClick={imprimir} disabled={imprimindo}>
                {imprimindo ? "Gerando…" : "Imprimir em PDF"}
              </Btn>
            )}
            {podeEditar && (
              <Btn variant="secondary" onClick={baixarFicha} disabled={gerandoFicha}>
                {gerandoFicha ? "Gerando…" : "Ficha de coleta em papel"}
              </Btn>
            )}
            {podeEditar && !semModalidade && (
              <Btn onClick={() => setEditando({ data: HOJE() })}>Nova coleta</Btn>
            )}
          </div>
        </div>

        {semModalidade && (
          <div style={{ marginTop: ESP.lg }}>
            <StatusBox tipo="aviso" msg={
              "Falta a modalidade no cadastro deste atleta. A bateria de testes é por "
              + "modalidade, e sem ela não há como montar a ficha de coleta."
            } />
          </div>
        )}
        {semNascimento && (
          <div style={{ marginTop: ESP.lg }}>
            <StatusBox tipo="aviso" msg={
              "Falta a data de nascimento no cadastro. Os testes podem ser lançados, mas a "
              + "tabela de conversão é por faixa de idade, então a sugestão de nota não "
          + "é apresentada."
            } />
          </div>
        )}
      </Card>

      {editando && podeEditar && (
        <FormularioDeColeta
          valores={editando}
          atleta={atleta}
          tecnicas={tecnicas}
          nucleo={nucleo}
          isMobile={isMobile}
          onCancelar={() => setEditando(null)}
          onSalvou={async () => { setEditando(null); await onSalvou(); }}
          onErro={onErro}
        />
      )}

      {serie.length === 0 ? (
        <Card><Vazio titulo="Este atleta ainda não tem teste físico." /></Card>
      ) : (
        <>
          <Card titulo="Resultados por teste">
            <TabelaDeTestes serie={serie} />
            <ComoLer serie={serie} />
            {podeEditar && (
              <div style={{ display: "flex", gap: 8, marginTop: ESP.lg, flexWrap: "wrap" }}>
                {[...serie].reverse().map(p => (
                  <Btn key={p.data} variant="discreto" tamanho="sm"
                    onClick={() => setEditando(coletas.find(c => c.data === p.data))}>
                    Corrigir {emBR(p.data)}
                  </Btn>
                ))}
              </div>
            )}
          </Card>

          <Card titulo="Capacidades físicas">
            <TabelaDeCapacidades serie={serie} />
            <LegendaDaEscala />
          </Card>

          {ponto && (
            <Card titulo="Leitura da coleta">
              <div style={{ display: "grid", gap: ESP.lg }}>
                {serie.length > 1 && (
                  <div style={{ maxWidth: 260 }}>
                    <Sel label="Coleta" value={ponto.data} onChange={setAbertaEm}
                      options={[...serie].reverse().map(p => ({
                        value: p.data,
                        label: `${emBR(p.data)}${p.idade != null ? ` · ${pt(p.idade, 1)} anos` : ""}`,
                      }))} />
                  </div>
                )}
                <Detalhe ponto={ponto} laudo={laudo} isMobile={isMobile} />
              </div>
            </Card>
          )}

          <Card titulo="Escore global">
            <EscoreGlobal escore={escoreDoPonto || escoreGlobal({
              fisica: ponto ? { somatorio: ponto.somatorio, divisor: ponto.divisor } : null,
            })} />
            <LegendaDosNiveis />
          </Card>

          <Card titulo="Evolução">
            <GradeDeGraficos serie={serie} isMobile={isMobile} />
          </Card>

          <Card titulo="O que mudou">
            <OQueMudou serie={serie} />
          </Card>
        </>
      )}
    </div>
  );
}

// ── Resultados por teste ─────────────────────────────────────

function TabelaDeTestes({ serie }) {
  const ids = [];
  const testes = {};
  serie.forEach(p => p.testes.forEach(t => {
    if (!ids.includes(t.id)) ids.push(t.id);
    testes[t.id] = t;
  }));

  const em = (p, id) => p.testes.find(t => t.id === id) || null;

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", ...TIPO.apoio }}>
        <thead>
          <tr>
            <Th>Teste</Th>
            {serie.map((p, i) => (
              <Fragment key={p.data}>
                <Th>{emBR(p.data)}</Th>
                {i > 0 && <Th>evol.</Th>}
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {ids.map(id => {
            const meta = testes[id];
            return (
              <tr key={id}>
                <Td forte>
                  {meta.rotulo}
                  <span style={{ fontWeight: 400, color: CINZA_ESC }}>
                    {meta.unidade ? ` (${meta.unidade})` : ""}
                  </span>
                  <div style={{ ...TIPO.apoio, fontWeight: 400, color: CINZA_ESC }}>
                    {meta.capacidade}
                    {meta.direcao === "menor" ? ", menos é melhor" : ", mais é melhor"}
                  </div>
                </Td>
                {serie.map((p, i) => {
                  const t = em(p, id);
                  return (
                    <Fragment key={p.data}>
                      <Td>
                        {t && t.valor !== null ? (
                          <>
                            {pt(t.valor, casasDo(meta))}
                            {t.tentativas.length > 1 && (
                              <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                                {t.tentativas.map(v => pt(v, casasDo(meta))).join(" · ")}
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: CINZA_ESC }}>
                            {t ? rotuloDaAusencia(t.ausencia) : "—"}
                          </span>
                        )}
                      </Td>
                      {i > 0 && (
                        <Td>
                          <Evolucao valor={t ? t.evolucao : null}
                            classificacao={t ? t.classificacao : null} />
                        </Td>
                      )}
                    </Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── O protocolo do teste ─────────────────────────────────────
//
// Quem monta o circuito em campo precisa das medidas na mão, e um tempo
// medido numa montagem diferente não se compara com o de janeiro. Por
// isso o protocolo aparece na própria tela da coleta, e não só no manual.
function Protocolo({ protocolo }) {
  const [aberto, setAberto] = useState(false);
  return (
    <div style={{ marginTop: 8 }}>
      <button type="button" onClick={() => setAberto(a => !a)} style={{
        background: "none", border: "none", padding: 0, cursor: "pointer",
        ...TIPO.apoio, color: AZUL, textAlign: "left",
      }}>
        {aberto ? "Esconder o protocolo do teste" : "Como montar e aplicar este teste"}
      </button>
      {aberto && (
        <div style={{
          marginTop: 6, background: FUNDO_SUAVE, border: `1px solid ${BORDA}`,
          borderRadius: RAIO.md, padding: "10px 12px", ...TIPO.apoio, color: CINZA_ESC,
          lineHeight: 1.6,
        }}>
          <div>{protocolo.resumo}</div>
          {[
            { titulo: "Montagem", itens: protocolo.medidas },
            { titulo: "Execução", itens: protocolo.execucao },
          ].filter(b => Array.isArray(b.itens) && b.itens.length).map(b => (
            <div key={b.titulo} style={{ marginTop: 8 }}>
              <strong style={{ color: TEXTO, fontWeight: 600 }}>{b.titulo}</strong>
              <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
                {b.itens.map(i => <li key={i}>{i}</li>)}
              </ul>
            </div>
          ))}
          {protocolo.origem && (
            <div style={{ marginTop: 8, fontStyle: "italic" }}>{protocolo.origem}</div>
          )}
        </div>
      )}
    </div>
  );
}

function casasDo(teste) {
  return teste && teste.unidade === "s" ? 2 : teste && teste.unidade === "cm" ? 1 : 0;
}

function rotuloDaAusencia(codigo) {
  const marca = Object.values(AUSENCIAS).find(a => a.codigo === codigo);
  return marca ? marca.rotulo.toLowerCase() : "sem registro";
}

function Evolucao({ valor, classificacao }) {
  if (!Number.isFinite(valor)) return <span style={{ color: CINZA_ESC }}>—</span>;
  const cor = classificacao === "Melhorou" ? OK : classificacao === "Piorou" ? RUIM : CINZA_ESC;
  return (
    <span style={{ color: cor, fontWeight: 600 }}>
      {valor > 0 ? "+" : valor < 0 ? "−" : ""}{pt(Math.abs(valor), 1)}%
    </span>
  );
}

// ── Capacidades por nota ─────────────────────────────────────

function TabelaDeCapacidades({ serie }) {
  const ids = [];
  const rotulos = {};
  serie.forEach(p => p.capacidades.forEach(c => {
    if (!ids.includes(c.id)) ids.push(c.id);
    rotulos[c.id] = c.rotulo;
  }));

  const em = (p, id) => {
    const c = p.capacidades.find(x => x.id === id);
    return c && Number.isFinite(c.nota) ? c : null;
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", ...TIPO.apoio }}>
        <thead>
          <tr>
            <Th>Capacidade</Th>
            {serie.map((p, i) => (
              <Fragment key={p.data}>
                <Th>{emBR(p.data)}</Th>
                {i > 0 && <Th>dif.</Th>}
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {ids.map(id => (
            <tr key={id}>
              <Td forte>{rotulos[id]}</Td>
              {serie.map((p, i) => {
                const atual = em(p, id);
                const anterior = i > 0 ? em(serie[i - 1], id) : null;
                const dif = atual && anterior ? atual.nota - anterior.nota : null;
                return (
                  <Fragment key={p.data}>
                    {/* Uma casa sempre, como no relatório: "7" na tela
                        e "7,0" no papel obriga quem confere a parar. */}
                    <Td>{atual ? pt(atual.nota, 1) : "—"}</Td>
                    {i > 0 && (
                      <Td>
                        {Number.isFinite(dif) && dif !== 0 ? (
                          <span style={{ color: dif > 0 ? OK : RUIM, fontWeight: 600 }}>
                            {dif > 0 ? "+" : "−"}{pt(Math.abs(dif), Math.abs(dif) % 1 ? 1 : 0)}
                          </span>
                        ) : (
                          <span style={{ color: CINZA_ESC }}>{dif === 0 ? "=" : "—"}</span>
                        )}
                      </Td>
                    )}
                  </Fragment>
                );
              })}
            </tr>
          ))}
          <tr>
            <Td forte>Somatório</Td>
            {serie.map((p, i) => (
              <Fragment key={p.data}>
                <Td forte>{pt(p.somatorio, 1)}</Td>
                {i > 0 && <Td /> }
              </Fragment>
            ))}
          </tr>
          <tr>
            <Td forte>Média</Td>
            {serie.map((p, i) => {
              const faixa = faixaDaNotaDeCapacidade(p.media);
              return (
                <Fragment key={p.data}>
                  <Td forte>
                    {pt(p.media, 2)}
                    {faixa && (
                      <span style={{ ...TIPO.apoio, fontWeight: 400, color: CINZA_ESC }}>
                        {` ${faixa.rotulo.toLowerCase()}`}
                      </span>
                    )}
                  </Td>
                  {i > 0 && (
                    <Td>
                      {Number.isFinite(p.delta) ? (
                        <span style={{
                          color: p.delta > 0 ? OK : p.delta < 0 ? RUIM : CINZA_ESC,
                          fontWeight: 600,
                        }}>
                          {p.delta > 0 ? "+" : p.delta < 0 ? "−" : ""}{pt(Math.abs(p.delta), 2)}
                        </span>
                      ) : <span style={{ color: CINZA_ESC }}>—</span>}
                    </Td>
                  )}
                </Fragment>
              );
            })}
          </tr>
        </tbody>
      </table>
      <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: ESP.md, lineHeight: 1.6 }}>
        A média das capacidades é o que entra no escore global, com peso de 25%. Ela divide
        pelo número de capacidades da coleta, inclusive as que ficaram sem nota.
      </div>
    </div>
  );
}

// ── Como ler ─────────────────────────────────────────────────

function ComoLer({ serie }) {
  const [aberto, setAberto] = useState(false);
  // Os testes da modalidade, uma vez cada, na ordem em que aparecem.
  const testesDaSerie = [];
  serie.forEach(p => p.testes.forEach(t => {
    if (!testesDaSerie.some(x => x.id === t.id)) testesDaSerie.push(t);
  }));
  const temTesteDeTempo = testesDaSerie.some(t => t.direcao === "menor");
  const temTresTentativas = serie.some(p => p.testes.some(t => t.tentativas.length > 1));
  const temAusencia = serie.some(p => p.testes.some(
    t => t.valor === null && t.ausencia && t.ausencia !== "semRegistro",
  ));

  const verbetes = [
    {
      titulo: "A direção de cada teste",
      // Sai dos testes DESTA modalidade. A frase antiga citava os das
      // três de uma vez, e quem abria a tela do skate lia sobre o tempo
      // de pista do BMX.
      texto:
        `${direcaoEmTexto(testesDaSerie)} A coluna de evolução já sai com o sinal do `
        + "DESEMPENHO, e não do número"
        + (temTesteDeTempo
          ? ": cair de 25,94 s para 24,97 s aparece como melhora de 3,7%."
          : "."),
    },
    {
      titulo: "A faixa morta de 1%",
      texto:
        `Variação de até ${FAIXA_MORTA}% é lida como estável, e não como evolução. Meio `
        + "centímetro numa impulsão de 180 cm é 0,3%, e isso é o erro da trena. Sem essa "
        + "faixa, metade da equipe apareceria evoluindo em todo relatório.",
    },
  ];

  if (temTresTentativas) {
    verbetes.push({
      titulo: "As três tentativas",
      texto:
        "Vale a melhor das três, e as três ficam guardadas, escritas abaixo do resultado. "
        + "Elas informam o que a melhor tentativa isolada não informa: tentativas muito "
        + "diferentes entre si indicam que a melhora pode estar na tentativa, e não no "
        + "treino.",
    });
  }
  if (temAusencia) {
    verbetes.push({
      titulo: "Falta, dispensa médica e não realizado",
      texto:
        "São três situações distintas, e nenhuma delas equivale a zero. A ausência não "
        + "entra na média: zero é um resultado ruim, e a falta de resultado não é "
        + "resultado. Quem faltou numa coleta é comparado com a última em que teve "
        + "resultado.",
    });
  }
  verbetes.push({
    titulo: "De onde vem a sugestão de nota",
    texto:
      "A coordenação escreveu, no rodapé da planilha de performance, uma tabela que converte "
      + "resultado de teste em faixa de nota, por modalidade e por faixa de idade. A tela "
      + "mostra a faixa sugerida ao lado do campo, com a regra que a produziu. Ela sugere e "
      + "não preenche: a tabela cobre dois testes por modalidade e as capacidades são quatro.",
  });

  return (
    <div style={{ marginTop: ESP.md }}>
      <button onClick={() => setAberto(a => !a)} style={{
        background: "none", border: "none", padding: 0, cursor: "pointer",
        font: "inherit", ...TIPO.apoio, color: AZUL, fontWeight: 600,
      }}>
        {aberto ? "Esconder como ler" : "Como ler estes números"}
      </button>
      {aberto && (
        <div style={{ display: "grid", gap: ESP.md, marginTop: ESP.md }}>
          {verbetes.map(v => (
            <div key={v.titulo}>
              <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>{v.titulo}</div>
              <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>{v.texto}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Detalhe de uma coleta ────────────────────────────────────

function Detalhe({ ponto, laudo, isMobile }) {
  const medidos = ponto.testes.filter(t => t.valor !== null);
  // A sugestão guarda o id da capacidade, que é chave de banco. Quem lê
  // precisa do rótulo, e ele está na lista de capacidades da coleta.
  const rotuloDaCapacidade = id => {
    const c = ponto.capacidades.find(x => x.id === id);
    return c ? c.rotulo.toLowerCase() : id;
  };
  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      <FileiraDeEstatisticas>
        <Estatistica rotulo="Testes com resultado" valor={`${medidos.length} de ${ponto.testes.length}`} />
        <Estatistica rotulo="Média das capacidades" valor={pt(ponto.media, 2)} />
        <Estatistica rotulo="Faixa de idade" valor={ponto.faixaEtaria ? ponto.faixaEtaria.rotulo : "—"} />
        <Estatistica rotulo="Avaliador" valor={ponto.avaliador || "não informado"} />
      </FileiraDeEstatisticas>

      {!ponto.completa && ponto.faltam.length > 0 && (
        <StatusBox tipo="aviso" msg={
          `Esta coleta ficou incompleta em ${ponto.faltam.length} `
          + `ite${ponto.faltam.length > 1 ? "ns" : "m"}: ${ponto.faltam.join(", ")}.`
        } />
      )}

      {laudo && (
        <div>
          <div style={{ ...TIPO.corpo, color: TEXTO, lineHeight: 1.65 }}>{laudo.texto}</div>
          <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6, marginTop: ESP.sm }}>
            {laudo.ressalva}
          </div>
        </div>
      )}

      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(230px, 1fr))",
      }}>
        {ponto.testes.map(t => (
          <div key={t.id} style={{
            border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "10px 12px",
            background: FUNDO_SUAVE,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>{t.rotulo}</span>
              <span style={{ ...TIPO.corpo, fontWeight: 700, color: TEXTO }}>
                {t.valor !== null ? `${pt(t.valor, casasDo(t))} ${t.unidade}` : "—"}
              </span>
            </div>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>{t.capacidade}</div>
            {t.valor === null && (
              <div style={{ ...TIPO.apoio, color: ATENCAO }}>{rotuloDaAusencia(t.ausencia)}</div>
            )}
            {t.tentativas.length > 1 && (
              <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 4 }}>
                Tentativas: {t.tentativas.map(v => pt(v, casasDo(t))).join(", ")}.
                Dispersão de {pt(t.amplitude, casasDo(t))} {t.unidade}.
              </div>
            )}
            {t.sugestao && (
              <div style={{ ...TIPO.apoio, color: AZUL, marginTop: 4, lineHeight: 1.5 }}>
                A tabela sugere nota entre {t.sugestao.de} e {t.sugestao.ate}
                {t.sugestao.capacidade ? ` em ${rotuloDaCapacidade(t.sugestao.capacidade)}` : ""}.
                <div style={{ color: CINZA_ESC }}>{t.sugestao.regra}</div>
              </div>
            )}
            {(t.criterios || []).length > 0 && (
              <div style={{ marginTop: 6, ...TIPO.apoio }}>
                {t.criterios.map(c => (
                  <div key={c.id} style={{
                    display: "flex", justifyContent: "space-between", gap: 8, lineHeight: 1.7,
                  }}>
                    <span style={{ color: CINZA_ESC }}>{c.rotulo}</span>
                    <span style={{ color: TEXTO, fontWeight: 600 }}>
                      {c.nota === null || c.nota === undefined
                        ? "—"
                        : `${pt(c.nota, 1)}${c.faixa ? ` · ${c.faixa.rotulo}` : ""}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {t.foraDaBateria && (
              <div style={{ ...TIPO.apoio, color: ATENCAO, marginTop: 4 }}>
                Teste fora da bateria desta modalidade, registrado ainda assim.
              </div>
            )}
          </div>
        ))}
      </div>

      {ponto.observacoes && (
        <div>
          <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>
            Observações do avaliador
          </div>
          <div style={{ ...TIPO.corpo, color: TEXTO, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
            {ponto.observacoes}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Evolução em gráfico ──────────────────────────────────────
//
// Um gráfico por teste, com a escala de cada medida, como na avaliação
// física: centímetro de impulsão e segundo de circuito não cabem no mesmo
// eixo, e duas medidas no mesmo desenho se aproximariam por causa da
// escala escolhida e não por causa do atleta.
function GradeDeGraficos({ serie, isMobile }) {
  const ids = [];
  const metas = {};
  serie.forEach(p => p.testes.forEach(t => {
    if (!ids.includes(t.id)) ids.push(t.id);
    metas[t.id] = t;
  }));

  const indicadores = [
    ...ids.map(id => ({
      id,
      rotulo: metas[id].rotulo,
      unidade: metas[id].unidade,
      casas: casasDo(metas[id]),
      // A amplitude mínima por tipo de medida, pela mesma razão da
      // avaliação física: sem ela, 0,3 s de diferença ocupa a altura
      // inteira do desenho e vira salto aos olhos de quem lê de longe.
      amplitudeMinima: metas[id].unidade === "s" ? 2 : metas[id].unidade === "cm" ? 20 : 10,
      passo: metas[id].unidade === "s" ? 0.5 : 5,
      valor: p => {
        const t = p.testes.find(x => x.id === id);
        return t ? t.valor : null;
      },
    })),
    {
      id: "capacidades",
      rotulo: "Média das capacidades",
      unidade: "",
      casas: 2,
      amplitudeMinima: 2,
      passo: 0.5,
      valor: p => p.media,
    },
  ];

  return (
    <>
      <div style={{
        display: "grid", gap: ESP.xl,
        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
      }}>
        {indicadores.map(ind => (
          <GraficoDeMedida key={ind.id} serie={serie} indicador={ind} />
        ))}
      </div>
      <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: ESP.md, lineHeight: 1.6 }}>
        Cada teste tem o seu desenho e a sua escala, e a escala não comprime a variação:
        ela nunca é menor que o dobro do que o atleta variou, para que diferença pequena
        não resulte em curva acentuada. O desenho liga as coletas por segmento reto,
        porque não há medição entre duas datas.
      </div>
    </>
  );
}

// ── O que mudou ──────────────────────────────────────────────

function OQueMudou({ serie }) {
  const ultimo = serie[serie.length - 1];
  if (!ultimo || serie.length < 2) {
    return <Vazio titulo="A comparação aparece a partir da segunda coleta." />;
  }
  const comparaveis = ultimo.testes.filter(t => Number.isFinite(t.evolucao));
  const desdeOInicio = ultimo.testes.filter(t => Number.isFinite(t.desdeOInicio));

  return (
    <div style={{ display: "grid", gap: ESP.xl }}>
      <Bloco
        titulo="Desde a coleta anterior"
        itens={comparaveis.map(t => ({
          rotulo: t.rotulo,
          valor: t.evolucao,
          classificacao: t.classificacao,
          nota: t.compararamCom && t.compararamCom !== serie[serie.length - 2].data
            ? `comparado com ${emBR(t.compararamCom)}, que é a última coleta com resultado`
            : null,
        }))}
        extra={Number.isFinite(ultimo.delta)
          ? { rotulo: "Média das capacidades", pontos: ultimo.delta }
          : null}
      />
      {serie.length > 2 && (
        <Bloco
          titulo="Desde o início"
          itens={desdeOInicio.map(t => ({
            rotulo: t.rotulo,
            valor: t.desdeOInicio,
            classificacao: t.classificacaoDesdeOInicio,
            nota: t.dataDoInicio ? `desde ${emBR(t.dataDoInicio)}` : null,
          }))}
          extra={Number.isFinite(ultimo.desdeOInicio)
            ? { rotulo: "Média das capacidades", pontos: ultimo.desdeOInicio }
            : null}
        />
      )}
    </div>
  );
}

function Bloco({ titulo, itens, extra }) {
  if (!itens.length && !extra) return null;
  return (
    <div>
      <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700 }}>{titulo}</div>
      <div style={{
        display: "grid", gap: 8, marginTop: ESP.md,
        gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
      }}>
        {extra && (
          <div style={{ border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "9px 11px" }}>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>{extra.rotulo}</div>
            <div style={{
              ...TIPO.corpo, fontWeight: 700,
              color: extra.pontos > 0 ? OK : extra.pontos < 0 ? RUIM : CINZA_ESC,
            }}>
              {extra.pontos > 0 ? "+" : extra.pontos < 0 ? "−" : ""}
              {pt(Math.abs(extra.pontos), 2)}
              <span style={{ ...TIPO.apoio, fontWeight: 400, color: CINZA_ESC }}> pontos</span>
            </div>
          </div>
        )}
        {itens.map(i => (
          <div key={i.rotulo} style={{
            border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "9px 11px",
          }}>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>{i.rotulo}</div>
            <div style={{
              ...TIPO.corpo, fontWeight: 700,
              color: i.classificacao === "Melhorou" ? OK
                : i.classificacao === "Piorou" ? RUIM : CINZA_ESC,
            }}>
              {i.valor > 0 ? "+" : i.valor < 0 ? "−" : ""}{pt(Math.abs(i.valor), 1)}%
              <span style={{ ...TIPO.apoio, fontWeight: 400, color: CINZA_ESC }}>
                {i.classificacao ? ` ${i.classificacao.toLowerCase()}` : ""}
              </span>
            </div>
            {i.nota && (
              <div style={{ ...TIPO.apoio, color: ATENCAO, lineHeight: 1.5 }}>{i.nota}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Formulário ───────────────────────────────────────────────

function FormularioDeColeta({
  valores, atleta, tecnicas, nucleo, isMobile, onCancelar, onSalvou, onErro,
}) {
  const modalidade = atleta.modalidade || "";
  const bateria = bateriaDa(modalidade);

  const [form, setForm] = useState(() => {
    const gravadas = Array.isArray(valores.capacidades) && valores.capacidades.length
      ? valores.capacidades
      : capacidadesSugeridas(modalidade);
    // Cada teste tem três tentativas na tela. O documento aceita as três
    // num array ou um valor só, e o formulário sempre manda as três, que
    // é como a coleta acontece no papel.
    const resultados = {};
    bateria.forEach(t => {
      const bruto = (valores.resultados || {})[t.id];
      if (Array.isArray(bruto)) {
        resultados[t.id] = { tentativas: [0, 1, 2].map(i => bruto[i] ?? ""), marca: "" };
      } else if (typeof bruto === "number") {
        resultados[t.id] = { tentativas: [String(bruto), "", ""], marca: "" };
      } else if (typeof bruto === "string" && bruto) {
        const marca = MARCAS.find(m => m.value && m.value === bruto.toLowerCase());
        resultados[t.id] = marca
          ? { tentativas: ["", "", ""], marca: marca.value }
          : { tentativas: [bruto, "", ""], marca: "" };
      } else {
        resultados[t.id] = { tentativas: ["", "", ""], marca: "" };
      }
    });
    // ── Os critérios de dentro do teste ───────────────────
    //
    // Alguns testes rendem mais que o número do cronômetro. O circuito
    // do tênis de mesa é um: o tempo é um só, e o avaliador vê cinco
    // coisas enquanto o atleta corre. Elas ficam no teste, e não nas
    // capacidades, porque são a leitura daquele teste e não entram no
    // escore global.
    //
    // A lista vem do documento quando já existe, e da definição do teste
    // quando é coleta nova. Ler do documento é o que deixa uma coleta
    // antiga intacta quando a definição do teste muda depois.
    const criteriosDoTeste = {};
    bateria.forEach(teste => {
      if (!Array.isArray(teste.criterios) || !teste.criterios.length) return;
      const gravado = ((valores.criteriosDoTeste || {})[teste.id] || []);
      const porId = Object.fromEntries(gravado.map(c => [c.id, c]));
      const base = gravado.length ? gravado : teste.criterios;
      criteriosDoTeste[teste.id] = base.map(c => ({
        id: c.id,
        rotulo: c.rotulo,
        detalhe: c.detalhe || (teste.criterios.find(x => x.id === c.id) || {}).detalhe || "",
        nota: (porId[c.id] || {}).nota ?? "",
      }));
    });
    return {
      data: valores.data || HOJE(),
      avaliador: valores.avaliador || "",
      observacoes: valores.observacoes || "",
      resultados,
      criteriosDoTeste,
      capacidades: gravadas.map(c => ({ ...c, nota: c.nota ?? "" })),
    };
  });
  const [salvando, setSalvando] = useState(false);
  const [insistindo, setInsistindo] = useState(false);

  const idade = idadeDecimalEm(atleta.dataNascimento, form.data);
  const faixa = faixaEtaria(idade);

  const mudar = (campo, v) => setForm(f => ({ ...f, [campo]: v }));
  const mudarTentativa = (id, i, v) => setForm(f => ({
    ...f,
    resultados: {
      ...f.resultados,
      [id]: {
        ...f.resultados[id],
        tentativas: f.resultados[id].tentativas.map((t, j) => (j === i ? v : t)),
      },
    },
  }));
  const mudarMarca = (id, v) => setForm(f => ({
    ...f,
    resultados: { ...f.resultados, [id]: { ...f.resultados[id], marca: v } },
  }));
  const mudarNotaDoTeste = (teste, id, v) => setForm(f => ({
    ...f,
    criteriosDoTeste: {
      ...f.criteriosDoTeste,
      [teste]: f.criteriosDoTeste[teste].map(c => (c.id === id ? { ...c, nota: v } : c)),
    },
  }));
  const mudarNota = (id, v) => setForm(f => ({
    ...f,
    capacidades: f.capacidades.map(c => (c.id === id ? { ...c, nota: v } : c)),
  }));

  // A melhor tentativa e a sugestão da tabela, recalculadas a cada
  // digitação. É o que faz o avaliador ver a faixa sugerida enquanto
  // ainda está com o atleta na frente.
  const porTeste = bateria.map(t => {
    const r = form.resultados[t.id];
    const numeros = r.tentativas
      .map(v => Number(String(v).replace(",", ".")))
      .filter(n => Number.isFinite(n) && n > 0);
    const melhor = numeros.length
      ? (t.direcao === "menor" ? Math.min(...numeros) : Math.max(...numeros))
      : null;
    return {
      teste: t,
      melhor,
      amplitude: numeros.length > 1 ? Math.max(...numeros) - Math.min(...numeros) : null,
      sugestao: melhor !== null
        ? sugestaoDeNota({ modalidade, idade, teste: t.id, valor: melhor })
        : null,
      marca: r.marca,
    };
  });
  const sugestaoPorCapacidade = {};
  porTeste.forEach(x => {
    if (x.sugestao && x.sugestao.capacidade) {
      sugestaoPorCapacidade[x.sugestao.capacidade] = { ...x.sugestao, teste: x.teste };
    }
  });

  const tecnicaDoDia = tecnicas.find(t => t.data === form.data) || null;

  const pendencias = [
    ...porTeste
      .filter(x => x.melhor === null && !x.marca)
      .map(x => x.teste.rotulo),
    ...form.capacidades
      .filter(c => !String(c.nota ?? "").trim())
      .map(c => c.rotulo),
    ...Object.entries(form.criteriosDoTeste).flatMap(([id, lista]) => lista
      .filter(c => !String(c.nota ?? "").trim())
      .map(c => `${c.rotulo} (${(TESTE_POR_ID[id] || {}).rotulo || id})`)),
  ];
  const fora = c => {
    const n = Number(String(c.nota ?? "").replace(",", "."));
    return String(c.nota ?? "").trim() && (!Number.isFinite(n) || n < 1 || n > 10);
  };
  const foraDaEscala = [
    ...form.capacidades.filter(fora),
    ...Object.values(form.criteriosDoTeste).flatMap(lista => lista.filter(fora)),
  ];

  async function salvar() {
    if (!form.data) return onErro("A coleta precisa de uma data.");
    if (foraDaEscala.length) {
      return onErro(
        `A escala de nota vai de 1 a 10, e ${foraDaEscala.map(c => c.rotulo).join(", ")} `
        + "está fora dela.",
      );
    }
    if (pendencias.length && !insistindo) {
      setInsistindo(true);
      return onErro(
        `Faltam ${pendencias.length} ite${pendencias.length > 1 ? "ns" : "m"} para a coleta `
        + `ficar completa: ${pendencias.join(", ")}. Preencha o que faltar e grave `
        + "novamente, ou confirme a gravação. O teste não realizado tem marca própria, de "
        + "falta ou de dispensa médica, e ela registra mais informação que o campo vazio.",
      );
    }

    setSalvando(true);
    try {
      const resultados = {};
      bateria.forEach(t => {
        const r = form.resultados[t.id];
        if (r.marca) {
          resultados[t.id] = r.marca;
          return;
        }
        const numeros = r.tentativas
          .map(v => Number(String(v).replace(",", ".")))
          .filter(n => Number.isFinite(n) && n > 0);
        // As três tentativas vão gravadas, e não só a melhor. Quem
        // escolhe a melhor é a leitura, pela direção do teste, e isso
        // deixa a dispersão disponível para o relatório.
        if (numeros.length) resultados[t.id] = numeros;
      });

      await salvarTesteFisico({
        nucleo,
        atletaUid: atleta.uid,
        atletaNome: atleta.nome,
        modalidade,
        data: form.data,
        avaliador: form.avaliador.trim() || null,
        observacoes: form.observacoes.trim() || "",
        resultados,
        criteriosDoTeste: Object.fromEntries(
          Object.entries(form.criteriosDoTeste).map(([id, lista]) => [
            id,
            lista.map(c => {
              const saida = { id: c.id, rotulo: c.rotulo };
              const n = Number(String(c.nota ?? "").replace(",", "."));
              if (String(c.nota ?? "").trim() && Number.isFinite(n)) saida.nota = n;
              if (c.detalhe) saida.detalhe = c.detalhe;
              return saida;
            }),
          ]),
        ),
        capacidades: form.capacidades.map(c => {
          const saida = { id: c.id, rotulo: c.rotulo };
          const n = Number(String(c.nota ?? "").replace(",", "."));
          if (String(c.nota ?? "").trim() && Number.isFinite(n)) saida.nota = n;
          if (c.detalhe) saida.detalhe = c.detalhe;
          return saida;
        }),
        divisor: form.capacidades.length,
      });
      await onSalvou();
    } catch (e) {
      onErro(`Não consegui gravar: ${e.message}`);
    } finally {
      setSalvando(false);
    }
  }

  // As regras da tabela que valem para esta coleta. Uma por teste,
  // porque a faixa de idade é por teste: o circuito do tênis de mesa tem
  // as faixas dele, e a impulsão continua nas do programa.
  const regrasDaFaixa = bateria.flatMap(teste => {
    const f = faixaEtariaDe(idade, teste.id);
    if (!f) return [];
    const regras = (CONVERSOES[modalidade] || {})[f.id] || [];
    return regras.filter(r => r.teste === teste.id).map(r => ({ ...r, faixa: f }));
  });

  return (
    <Card titulo="Coleta de testes físicos">
      <div style={{ display: "grid", gap: ESP.lg }}>
        <div style={{
          display: "grid", gap: 10,
          gridTemplateColumns: isMobile ? "1fr" : "190px minmax(200px, 1fr) 190px",
        }}>
          <Input label="Data da coleta" type="date" value={form.data}
            onChange={v => mudar("data", v)} />
          <Input label="Avaliador" value={form.avaliador}
            onChange={v => mudar("avaliador", v)} placeholder="quem aplicou os testes" />
          <div>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 20 }}>
              {Number.isFinite(idade)
                ? `${pt(idade, 1)} anos na data, faixa ${faixa ? faixa.rotulo : "não definida"}`
                : "sem data de nascimento no cadastro"}
            </div>
          </div>
        </div>

        <div>
          <h4 style={{ margin: "0 0 8px", ...TIPO.corpo, color: TEXTO }}>
            Testes medidos
            <span style={{ fontWeight: 400, color: CINZA_ESC }}> (três tentativas, vale a melhor)</span>
          </h4>
          <div style={{ display: "grid", gap: 10 }}>
            {porTeste.map(({ teste, melhor, amplitude, sugestao, marca }) => (
              <div key={teste.id} style={{
                border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "10px 12px",
              }}>
                <div style={{
                  display: "grid", gap: 10, alignItems: "end",
                  gridTemplateColumns: isMobile
                    ? "1fr 1fr"
                    : "minmax(180px, 1.4fr) 90px 90px 90px minmax(140px, 1fr)",
                }}>
                  <div>
                    <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>
                      {teste.rotulo}
                      <span style={{ fontWeight: 400, color: CINZA_ESC }}>
                        {teste.unidade ? ` (${teste.unidade})` : ""}
                      </span>
                    </div>
                    <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                      {teste.capacidade}
                      {teste.direcao === "menor" ? ", menos é melhor" : ", mais é melhor"}
                    </div>
                  </div>
                  {[0, 1, 2].map(i => (
                    <Input key={i} label={`${i + 1}ª`} type="number"
                      value={form.resultados[teste.id].tentativas[i]}
                      onChange={v => mudarTentativa(teste.id, i, v)} />
                  ))}
                  <Sel label="Sem resultado" value={marca}
                    onChange={v => mudarMarca(teste.id, v)} options={MARCAS} />
                </div>
                {teste.protocolo && <Protocolo protocolo={teste.protocolo} />}
                <div style={{ ...TIPO.apoio, marginTop: 6, color: CINZA_ESC }}>
                  {marca ? (
                    <span style={{ color: ATENCAO }}>
                      Marcado como {MARCAS.find(m => m.value === marca).label.toLowerCase()}.
                      As tentativas digitadas não serão gravadas.
                    </span>
                  ) : melhor !== null ? (
                    <>
                      Melhor: <strong style={{ color: TEXTO }}>{pt(melhor, casasDo(teste))} {teste.unidade}</strong>
                      {amplitude !== null && ` · dispersão de ${pt(amplitude, casasDo(teste))} ${teste.unidade}`}
                      {sugestao && (
                        <span style={{ color: AZUL }}>
                          {` · a tabela sugere nota entre ${sugestao.de} e ${sugestao.ate}`}
                          {sugestao.capacidade ? "" : ", sem capacidade associada"}
                        </span>
                      )}
                    </>
                  ) : (
                    "sem resultado digitado"
                  )}
                </div>
                {(form.criteriosDoTeste[teste.id] || []).length > 0 && (
                  <div style={{ marginTop: 10, borderTop: `1px solid ${BORDA}`, paddingTop: 10 }}>
                    <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>
                      Critérios observados no teste
                      <span style={{ fontWeight: 400, color: CINZA_ESC }}>
                        {" "}(nota de 1 a 10, fora do escore global)
                      </span>
                    </div>
                    <div style={{
                      display: "grid", gap: 10, marginTop: 8,
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "repeat(auto-fill, minmax(215px, 1fr))",
                    }}>
                      {form.criteriosDoTeste[teste.id].map(c => {
                        const n = Number(String(c.nota ?? "").replace(",", "."));
                        const faixaDaNota = faixaDaNotaDeCapacidade(n);
                        const pendente = !String(c.nota ?? "").trim();
                        return (
                          <div key={c.id}>
                            <Input label={c.rotulo} type="number" value={c.nota}
                              onChange={v => mudarNotaDoTeste(teste.id, c.id, v)} />
                            <div style={{
                              ...TIPO.apoio, marginTop: 2, lineHeight: 1.5,
                              color: pendente ? ATENCAO : CINZA_ESC,
                            }}>
                              {pendente ? "sem nota" : faixaDaNota ? faixaDaNota.rotulo : ""}
                              {c.detalhe && <div>{c.detalhe}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 style={{ margin: "0 0 8px", ...TIPO.corpo, color: TEXTO }}>
            Capacidades físicas
            <span style={{ fontWeight: 400, color: CINZA_ESC }}> (nota de 1 a 10)</span>
          </h4>
          <LegendaDaEscala />
          <div style={{
            display: "grid", gap: 10, marginTop: ESP.md,
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(230px, 1fr))",
          }}>
            {form.capacidades.map(c => {
              const n = Number(String(c.nota ?? "").replace(",", "."));
              const faixaDaNota = faixaDaNotaDeCapacidade(n);
              const sug = sugestaoPorCapacidade[c.id] || null;
              const pendente = !String(c.nota ?? "").trim();
              return (
                <div key={c.id} style={{
                  border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "10px 11px",
                }}>
                  <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>
                    {c.rotulo}
                    {c.detalhe && (
                      <span style={{ fontWeight: 400, color: CINZA_ESC }}>{` · ${c.detalhe}`}</span>
                    )}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <Input label="Nota" type="number" value={c.nota}
                      onChange={v => mudarNota(c.id, v)} />
                  </div>
                  <div style={{
                    ...TIPO.apoio, marginTop: 2, color: pendente ? ATENCAO : CINZA_ESC,
                  }}>
                    {pendente ? "sem nota" : faixaDaNota ? faixaDaNota.rotulo : ""}
                  </div>
                  {sug && (
                    <div style={{ ...TIPO.apoio, color: AZUL, marginTop: 4, lineHeight: 1.5 }}>
                      {`O ${sug.teste.rotulo.toLowerCase()} de ${pt(sug.valor, casasDo(sug.teste))} `}
                      {`${sug.teste.unidade} sugere entre ${sug.de} e ${sug.ate}.`}
                      <div style={{ color: CINZA_ESC }}>{sug.regra}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {regrasDaFaixa.length > 0 && (
            <div style={{
              marginTop: ESP.md, background: FUNDO_SUAVE, border: `1px solid ${BORDA}`,
              borderRadius: RAIO.md, padding: "10px 12px",
            }}>
              <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>
                A tabela de conversão de {modalidade}
              </div>
              {regrasDaFaixa.map(r => (
                <div key={r.teste} style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>
                  <strong style={{ color: TEXTO, fontWeight: 600 }}>{r.faixa.rotulo}: </strong>
                  {r.texto}
                  {r.capacidade ? "" : " (sem capacidade associada nesta modalidade)"}
                </div>
              ))}
              <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 6, lineHeight: 1.6 }}>
                A tabela é referência; a nota é decidida pelo avaliador. Ela cobre dois
                testes por modalidade, e as capacidades são quatro.
              </div>
            </div>
          )}
        </div>

        <Input label="Observações" value={form.observacoes}
          onChange={v => mudar("observacoes", v)} placeholder="o que não cabe em número" />

        <StatusBox tipo={tecnicaDoDia ? "ok" : "aviso"} msg={
          tecnicaDoDia
            ? "A avaliação técnica desta data já foi lançada, então o escore global deste "
              + "atleta será calculado."
            : "Ainda não há avaliação técnica lançada nesta data. O escore global é 25% desta "
              + "coleta e 75% da avaliação técnica, e não é calculado sem as duas partes."
        } />

        {pendencias.length > 0 && (
          <StatusBox tipo={insistindo ? "erro" : "aviso"} msg={
            `Faltam ${pendencias.length} ite${pendencias.length > 1 ? "ns" : "m"}: `
            + `${pendencias.join(", ")}.`
            + (insistindo
              ? " Gravar novamente registra a coleta incompleta, e o que depende destes"
                + " campos não é calculado."
              : "")
          } />
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={salvar} disabled={salvando}>
            {salvando
              ? "Gravando..."
              : insistindo && pendencias.length
                ? `Gravar incompleta (faltam ${pendencias.length})`
                : "Gravar coleta"}
          </Btn>
          <Btn variant="secondary" onClick={onCancelar} disabled={salvando}>Cancelar</Btn>
        </div>
      </div>
    </Card>
  );
}

// ── Apoio ────────────────────────────────────────────────────

function Th({ children }) {
  return (
    <th style={{
      textAlign: "left", padding: "7px 9px", borderBottom: `1px solid ${BORDA}`,
      color: CINZA_ESC, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {children}
    </th>
  );
}

function Td({ children, forte }) {
  return (
    <td style={{
      padding: "7px 9px", borderBottom: `1px solid ${BORDA}`,
      color: TEXTO, fontWeight: forte ? 700 : 400, whiteSpace: "nowrap",
    }}>
      {children}
    </td>
  );
}

function pt(v, casas = 1) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(casas).replace(".", ",") : "—";
}

function emBR(iso) {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return d ? `${d}/${m}/${a}` : iso;
}
